import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import { compose } from 'react-apollo';
import cn from 'classnames';
import FocusLock, { AutoFocusInside } from 'react-focus-lock';
import {
  HeaderContainer,
  FooterContainer,
  InnerContainer,
} from 'chameleon/packages/blue-book-player/player';
import questionDetailsStyles from 'chameleon-src/components/blueBook/css/BlueBookQuestionsDetails.css';
import charmBraceletStyles from 'chameleon-src/components/blueBook/css/BlueBookCharmBracelet.css';
import { isMobile } from 'chameleon/packages/blue-book-player/mobile/utils';
import LearnosityProvider from './LearnosityProvider';
import { BluebookContext } from '../state/BluebookContext';
import { SET_IS_REVIEW_PAGE, SET_PLAYER } from '../state/reducer';
import styles from '../styles.css';

export const withBlueBookPlayer = (WrappedComponent) => {
  const BlueBookPlayerWrapper = (props) => {
    const {
      userData,
      previewData,
      params,
      assignmentId,
      isPreviewPlayer,
      isSingleQuestionPreview,
      isModal = false,
      isStudent,
      isImpersonating,
      render,
      bluebookUiHighlightEnabled,
      subject,
      onItemChange,
    } = props;

    const [learnosityApp, setLearnosityApp] = useState();
    const [learnosityItems, setLearnosityItems] = useState();
    const { state: playerState, dispatch } = useContext(BluebookContext);
    const isStudentAccount = isStudent && !isImpersonating;
    const isDigitalExam = subject.useBluebookDigitalExam;
    const { assignmentPlayer } = playerState;
    const { isSecure = false } = assignmentPlayer || {};

    useEffect(() => {
      // Learnosity doesnt clean the popups after switching assignemnts,
      // so we need to do it manually
      document
        .querySelectorAll('.lrn.lrn_widget.lrn_charactermap_wrapper')
        .forEach((el) => el.remove());
    }, []);

    useEffect(() => {
      if (playerState.isTestReady) {
        const items = learnosityApp?.getItems();
        setLearnosityItems(items);
      }
    }, [playerState.isTestReady, learnosityApp]);

    const testReadyListener = (leanosityApi) => {
      //In this stage The leanosityHandler api is not fully loaded/constructed
      setLearnosityApp(leanosityApi);

      const reviewPageParam = localStorage.getItem('isReviewPage') === 'true';

      dispatch({
        type: SET_PLAYER,
        payload: {
          isTestReady: true,
          //On test ready the questions() returns the first chunk lazy loaded API
          firstChunkQuestionsApi: leanosityApi?.questions() || {},
        },
      });

      setTimeout(() => {
        dispatch({
          type: SET_IS_REVIEW_PAGE,
          payload: !!reviewPageParam,
        });
      });
    };

    const onReadyListener = (leanosityApi) => {
      //In this stage The leanosityHandler api fully loaded/constructed
      setLearnosityApp(leanosityApi);
      dispatch({
        type: SET_PLAYER,
        payload: {
          isLearnosityApiReady: true,
        },
      });
    };

    const playerHeightClass = isImpersonating
      ? 'h-[calc(100vh-5.2rem)]'
      : 'h-screen';

    useEffect(() => {
      const documentHeight = throttle(() => {
        const doc = document.documentElement;
        doc.style.setProperty('--doc-height', `${window.innerHeight}px`);
      }, 200);

      window.addEventListener('resize', documentHeight);
      documentHeight();
      return () => window.removeEventListener('resize', documentHeight);
    }, []);

    return (
      <FocusLock group="student-player-group">
        <AutoFocusInside>
          <div
            className={cn(
              styles['bluebook-player'],
              questionDetailsStyles['bluebook-is-teacher-preview'],
              charmBraceletStyles['bluebook-is-teacher-preview'],
              playerHeightClass,
              { 'bluebook-is-impersonating': isImpersonating },
              { 'bluebook-is-teacher-preview': isPreviewPlayer },
              { 'bluebook-is-modal': isModal },
              { 'bluebook-is-mobile': isMobile },
              { 'bluebook-is-desktop': !isMobile },
              { 'bluebook-is-digital-exam': isDigitalExam },
              'flex flex-col w-full fixed',
            )}
            data-player="bluebook"
            data-cy="bluebook-player"
          >
            <HeaderContainer
              learnosityApp={learnosityApp}
              userData={userData}
              params={params}
              assignmentId={assignmentId}
              bluebookUiHighlightEnabled={bluebookUiHighlightEnabled}
              isPreviewPlayer={isPreviewPlayer}
              isImpersonating={isImpersonating}
              isDigitalExam={isDigitalExam}
            />
            <InnerContainer
              learnosityApp={learnosityApp}
              isReviewPage={playerState.isReviewPage}
              isSingleQuestionPreview={isSingleQuestionPreview}
              isStudentAccount={isStudentAccount}
              previewData={previewData}
              playerState={playerState}
              subject={subject}
              userData={userData}
              bluebookUiHighlightEnabled={bluebookUiHighlightEnabled}
              isSecure={isSecure}
            >
              {get(playerState, 'learnositySignedRequest') && (
                <LearnosityProvider
                  className="cb-learnosity-player relative z-belowModals"
                  shouldDestroy={false}
                  key="bluebook-learnosity-provider"
                  apiType="items"
                  initOptions={playerState.learnositySignedRequest}
                  testReadyListener={testReadyListener}
                  readyListener={onReadyListener}
                />
              )}
            </InnerContainer>
            <FooterContainer
              playerState={playerState}
              userData={userData}
              learnosityApp={learnosityApp}
              learnosityItems={learnosityItems}
              isPreviewPlayer={isPreviewPlayer}
              isSingleQuestionPreview={isSingleQuestionPreview}
              previewData={previewData}
              render={render}
              isDigitalExam={isDigitalExam}
              onItemChange={onItemChange}
              isSecure={isSecure}
            />
          </div>
          <WrappedComponent {...props} learnosityApp={learnosityApp} />
        </AutoFocusInside>
      </FocusLock>
    );
  };

  return compose(
    connect(({ subject: { data }, userData, featureFlag }) => ({
      subject: data,
      userData,
      bluebookUiHighlightEnabled: featureFlag.bluebook_ui_highlight_enabled,
    })),
  )(BlueBookPlayerWrapper);
};

export default withBlueBookPlayer;
